/**
 * Color utility functions for UI components
 */

/**
 * Convert hex color to RGB components
 */
export function hexToRgb(hex: number): { r: number; g: number; b: number } {
  return {
    r: (hex >> 16) & 0xff,
    g: (hex >> 8) & 0xff,
    b: hex & 0xff,
  };
}

/**
 * Convert RGB components to hex color
 */
export function rgbToHex(r: number, g: number, b: number): number {
  return ((r & 0xff) << 16) | ((g & 0xff) << 8) | (b & 0xff);
}

/**
 * Lighten a color by a percentage
 */
export function lightenColor(color: number, percent: number): number {
  const { r, g, b } = hexToRgb(color);
  const amount = Math.floor(255 * (percent / 100));

  return rgbToHex(
    Math.min(255, r + amount),
    Math.min(255, g + amount),
    Math.min(255, b + amount)
  );
}

/**
 * Darken a color by a percentage
 */
export function darkenColor(color: number, percent: number): number {
  const { r, g, b } = hexToRgb(color);
  const amount = Math.floor(255 * (percent / 100));

  return rgbToHex(
    Math.max(0, r - amount),
    Math.max(0, g - amount),
    Math.max(0, b - amount)
  );
}

/**
 * Adjust color brightness
 */
export function adjustBrightness(color: number, percent: number): number {
  if (percent > 0) {
    return lightenColor(color, percent);
  } else {
    return darkenColor(color, Math.abs(percent));
  }
}

/**
 * Convert hex color to CSS color string
 */
export function hexToString(hex: number): string {
  return `#${hex.toString(16).padStart(6, "0")}`;
}

/**
 * Interpolate between two colors
 */
export function lerpColor(
  colorA: number,
  colorB: number,
  t: number
): number {
  const a = hexToRgb(colorA);
  const b = hexToRgb(colorB);

  return rgbToHex(
    Math.round(a.r + (b.r - a.r) * t),
    Math.round(a.g + (b.g - a.g) * t),
    Math.round(a.b + (b.b - a.b) * t)
  );
}
